import Studio, { IStudio, IStudioApiModel } from './Studio.model';
import User, { IUser, IUserApiModel } from './User.model';

export enum InviteUserRole {
  ADMIN = 'admin',
  MEMBER = 'member',
  VIEWER = 'viewer',
  EDITOR = 'editor',
}

export enum InviteUserPanel {
  ADMIN = 'admin',
  APP = 'app',
}

export interface IInvite {
  id: string;
  email: string;
  accepted: boolean;
  canceled: boolean;
  role: InviteUserRole;
  panel: InviteUserPanel;
  studioId: string;
  studio?: IStudio;
  createdAt: Date;
  updatedAt: Date;
  user?: IUser;
  roleLevel: number;
}

export interface IInviteApiModel {
  id: string;
  email: string;
  accepted: boolean;
  canceled: boolean;
  role: InviteUserRole;
  panel: InviteUserPanel;
  studioId: string;
  studio?: IStudioApiModel;
  created_at: string;
  updated_at: string;
  user?: IUserApiModel;
}

export const roleDictionary = {
  admin: 0,
  editor: 1,
  member: 2,
  viewer: 3,
};

export const getRoleLevel = (role: string): number => {
  if (
    role === 'admin' ||
    role === 'editor' ||
    role === 'member' ||
    role === 'viewer'
  )
    return roleDictionary[role];
  return 99;
};

export default class Invite implements IInvite {
  public readonly id: string;

  public readonly email: string;

  public readonly accepted: boolean;

  public readonly canceled: boolean;

  public readonly role: InviteUserRole;

  public readonly panel: InviteUserPanel;

  public readonly studioId: string;

  public readonly studio?: IStudio;

  public readonly createdAt: Date;

  public readonly updatedAt: Date;

  public readonly user?: IUser;

  public readonly roleLevel: number;

  constructor(
    id: string,
    email: string,
    accepted: boolean,
    canceled: boolean,
    role: InviteUserRole,
    panel: InviteUserPanel,
    studioId: string,
    createdAt: Date,
    updatedAt: Date,
    roleLevel: number,
    studio?: IStudio,
    user?: IUser,
  ) {
    this.id = id;
    this.email = email;
    this.accepted = accepted;
    this.canceled = canceled;
    this.role = role;
    this.panel = panel;
    this.studioId = studioId;
    this.studio = studio;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.user = user;
    this.roleLevel = roleLevel;
  }

  static fromApiModel(apiModel: IInviteApiModel) {
    const { id, email, accepted, studioId, panel, role, canceled } = apiModel;

    const createdAt = new Date(Date.parse(apiModel.created_at));
    const updatedAt = new Date(Date.parse(apiModel.updated_at));
    const studio = apiModel.studio
      ? Studio.fromApiModel(apiModel.studio)
      : undefined;
    const user = apiModel.user ? User.fromApiModel(apiModel.user) : undefined;
    const roleLevel = getRoleLevel(role);

    return new Invite(
      id,
      email,
      accepted,
      canceled,
      role,
      panel,
      studioId,
      createdAt,
      updatedAt,
      roleLevel,
      studio,
      user,
    );
  }

  static arrayFromApiModel(arrayApiModel: IInviteApiModel[]) {
    const array = arrayApiModel.map(apiModel => {
      const { id, email, accepted, studioId, panel, role, canceled } = apiModel;

      const createdAt = new Date(Date.parse(apiModel.created_at));
      const updatedAt = new Date(Date.parse(apiModel.updated_at));
      const studio = apiModel.studio
        ? Studio.fromApiModel(apiModel.studio)
        : undefined;
      const user = apiModel.user ? User.fromApiModel(apiModel.user) : undefined;
      const roleLevel = getRoleLevel(role);

      return new Invite(
        id,
        email,
        accepted,
        canceled,
        role,
        panel,
        studioId,
        createdAt,
        updatedAt,
        roleLevel,
        studio,
        user,
      );
    });

    return array;
  }
}
